/**
 * FloatingCard — the hero stack on the Landing + Auth screens. A small
 * 4:5 photo card with the wydm wordmark + score badge + name + tagline,
 * rotated and absolutely positioned to look like loose Polaroids on a
 * desk.
 *
 * `primary` swaps to the heavier shadow lift so one card visually leads.
 */
import { CSSProperties } from 'react';
import { Photo } from './Photo';

type Props = {
  variant: 1 | 2 | 3 | 4 | 5 | 6;
  initial?: string;
  name: string;
  score: number;
  rotate?: number;
  top?: number;
  left?: number;
  right?: number;
  z?: number;
  primary?: boolean;
  style?: CSSProperties;
};

export function FloatingCard({
  variant,
  initial,
  name,
  score,
  rotate = 0,
  top,
  left,
  right,
  z = 1,
  primary,
  style,
}: Props) {
  return (
    <div
      style={{
        position: 'absolute',
        top,
        left,
        right,
        zIndex: z,
        width: 260,
        aspectRatio: '4/5',
        borderRadius: 24,
        overflow: 'hidden',
        transform: `rotate(${rotate}deg)`,
        boxShadow: primary ? 'var(--shadow-lift)' : 'var(--shadow-card)',
        ...style,
      }}
    >
      <Photo variant={variant} initial={initial} rounded={24} />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent 50%)',
        }}
      />
      {/* Wordmark */}
      <div style={{ position: 'absolute', top: 16, left: 16, display: 'flex', alignItems: 'center', gap: 4 }}>
        <div className="serif" style={{ fontSize: 14, fontStyle: 'italic', color: '#fff', fontWeight: 500 }}>wydm</div>
        <div style={{ width: 4, height: 4, borderRadius: 4, background: 'var(--coral)' }} />
      </div>
      {/* Score badge */}
      <div
        style={{
          position: 'absolute',
          top: 14,
          right: 14,
          padding: '5px 10px',
          background: 'rgba(255,255,255,0.94)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          borderRadius: 100,
          display: 'flex',
          alignItems: 'center',
          gap: 5,
        }}
      >
        <span className="serif" style={{ fontSize: 14, fontStyle: 'italic', fontWeight: 500, color: 'var(--coral)' }}>{score}</span>
      </div>
      <div style={{ position: 'absolute', bottom: 18, left: 18, color: '#fff' }}>
        <div className="serif" style={{ fontSize: 28, fontWeight: 500, lineHeight: 1 }}>{name}</div>
        <div
          className="mono"
          style={{ fontSize: 11, opacity: 0.85, marginTop: 4, letterSpacing: 1, textTransform: 'uppercase' }}
        >
          Would you date them?
        </div>
      </div>
    </div>
  );
}
